import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl, getRawDateString, getUsersEmail, isAdminRole } from '../authConfig';
import { toast } from 'react-toastify';
import { calculateAssigneeStats } from '../utils/StatsHelper';
import { useTaxPortal } from '../utils/useTaxPortal';
import CustomerDrawer from './CustomerDrawer';
import TaxPortalLayout from '../components/Common/TaxPortalLayout';
import TaxPortalToolbar from '../components/Common/TaxPortalToolbar';
import { formatPhoneNumber, formatSinNumber } from '../utils/formatters';
import Loader from '../components/Common/Loader';
import ESignDetailsModal from '../components/Common/ESignDetailsModal';
import ActionItemsDrawer from './ActionItemsDrawer';
import { getValidToken } from '../utils/msalTokenUtil';
import {
  FaChartBar, FaPhoneAlt, FaEdit, FaFolderOpen, FaCheckCircle, FaUsers, FaSignature,
  FaHourglassHalf, FaClipboardList, FaPaperPlane, FaFileSignature, FaExclamationCircle,
  FaMoneyBillWave, FaChevronLeft, FaChevronRight, FaInbox, FaSort, FaSortUp,
  FaSortDown, FaFileInvoiceDollar, FaEllipsisV, FaUserPlus,
} from 'react-icons/fa';

const statusOptions = ['Open', 'Pending', 'Review', 'Draft Sent', 'eSigned', 'Payment Pending', 'Payment Received', 'Completed'];

const dueDateOptions = [];
for (let i = 0; i < 5; i++) {
  const date = new Date();
  date.setDate(date.getDate() + i);
  dueDateOptions.push(date.toISOString().split('T')[0]);
}

const assignedToOptions = getUsersEmail();

const STATUS_META = {
  Open:               { color: '#64748b', bg: '#f1f5f9', icon: FaFolderOpen },
  Pending:            { color: '#b45309', bg: '#fef3c7', icon: FaHourglassHalf },
  Review:             { color: '#1d4ed8', bg: '#dbeafe', icon: FaClipboardList },
  'Draft Sent':       { color: '#0e7490', bg: '#cffafe', icon: FaPaperPlane },
  eSigned:            { color: '#6d28d9', bg: '#ede9fe', icon: FaFileSignature },
  'Payment Pending':  { color: '#b91c1c', bg: '#fee2e2', icon: FaExclamationCircle },
  'Payment Received': { color: '#0f766e', bg: '#ccfbf1', icon: FaMoneyBillWave },
  Completed:          { color: '#15803d', bg: '#dcfce7', icon: FaCheckCircle },
};

const getStatusMeta = (status) => STATUS_META[status] || { color: '#64748b', bg: '#f1f5f9', icon: FaFolderOpen };

// Avatar: 32px wide + gap-2 (8px) = 40px offset before name text
const AVATAR_SIZE = 32;
const AVATAR_GAP  = 8;  // gap-2
const CELL_PS     = 24; // ps-4 = 1.5rem = 24px on 16px base
// NAME th must be offset the same amount: CELL_PS + AVATAR_SIZE + AVATAR_GAP
const NAME_TH_PL  = CELL_PS + AVATAR_SIZE + AVATAR_GAP; // 64px

// Colour palette keyed on first chars of name for consistent colours
const AVATAR_COLORS = [
  { bg: '#dbeafe', text: '#1e40af' },
  { bg: '#fce7f3', text: '#9d174d' },
  { bg: '#d1fae5', text: '#065f46' },
  { bg: '#ede9fe', text: '#5b21b6' },
  { bg: '#fef3c7', text: '#92400e' },
  { bg: '#cffafe', text: '#164e63' },
  { bg: '#fee2e2', text: '#991b1b' },
  { bg: '#e0f2fe', text: '#0c4a6e' },
];
const getAvatarColor = (name = '') => {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};
const getInitials = (name = '') => {
  // Strip commas and the word "and" so "Sivasamy, Vijay and Nithya" → ["Sivasamy","Vijay","Nithya"]
  const clean = name.replace(/,/g, ' ').replace(/\band\b/gi, ' ');
  const parts = clean.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'U').substring(0, 2).toUpperCase();
};

const NameAvatar = ({ name }) => {
  const c = getAvatarColor(name);
  return (
    <span
      className="name-avatar flex-shrink-0"
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, backgroundColor: c.bg, color: c.text }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </span>
  );
};

const StatusBadge = ({ status, size = 'md' }) => {
  const meta = getStatusMeta(status);
  const Icon = meta.icon;
  const isSm = size === 'sm';
  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill fw-semibold"
      style={{
        color: meta.color,
        backgroundColor: meta.bg,
        fontSize: isSm ? '0.7rem' : '0.75rem',
        padding: isSm ? '3px 10px' : '5px 12px',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={isSm ? 9 : 10} />
      {status || 'Unknown'}
    </span>
  );
};

const SortIcon = ({ active, asc }) => {
  if (!active) return <FaSort className="ms-1 opacity-50" size={11} />;
  return asc ? <FaSortUp className="ms-1" size={11} /> : <FaSortDown className="ms-1" size={11} />;
};

// ══════════════ ACTION MENU (shared between desktop & mobile) ══════════════
// items: [{ label, icon, color, onClick, danger? }] or [{ divider: true }]
const ActionMenu = ({ items }) => (
  <div className="dropdown action-menu">
    <button
      className="action-menu-trigger"
      type="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      data-bs-boundary="window"
      aria-label="Actions"
      title="Actions"
    >
      <FaEllipsisV size={13} />
    </button>
    <ul className="dropdown-menu dropdown-menu-end action-menu-list shadow-sm">
      {items.map((item, i) => (
        item.divider ? (
          <li key={`div-${i}`}><hr className="dropdown-divider my-1" /></li>
        ) : (
          <li key={item.label}>
            <button
              className={`dropdown-item action-menu-item${item.danger ? ' text-danger' : ''}`}
              onClick={item.onClick}
            >
              <span className="action-menu-icon" style={{ color: item.danger ? '#dc2626' : item.color }}>
                <item.icon size={13} />
              </span>
              {item.label}
            </button>
          </li>
        )
      ))}
    </ul>
  </div>
);

const getId = (record) => record._id || record.id;

// Smart due-date pill
const DueDatePill = ({ dueDate, status }) => {
  if (status === 'Completed') return <span className="due-pill due-pill-normal">N/A</span>;
  if (!dueDate) return <span className="text-muted small">—</span>;
  try {
    const dueDateStr = new Date(dueDate).toISOString().split('T')[0];
    const todayStr   = new Date().toISOString().split('T')[0];
    if (dueDateStr === todayStr) {
      return (
        <span className="due-pill due-pill-today">
          <span className="pulse-dot" style={{ backgroundColor: '#d97706' }} />
          Today
        </span>
      );
    }
    if (dueDateStr < todayStr) {
      const diff = Math.round((new Date(todayStr) - new Date(dueDateStr)) / 86400000);
      return (
        <span className="due-pill due-pill-overdue">
          <FaExclamationCircle size={9} />
          {diff}d overdue
        </span>
      );
    }
  } catch { }
  return <span className="due-pill due-pill-normal">{getRawDateString(dueDate)}</span>;
};

// Skeleton shimmer rows
const SkeletonRows = ({ count = 7 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="skeleton-row">
        <td style={{ paddingLeft: CELL_PS }}>
          <div className="d-flex align-items-center gap-2">
            <span className="skeleton-cell" style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, borderRadius: '50%' }} />
            <span className="skeleton-cell" style={{ width: 100 + (i % 3) * 30, height: 14 }} />
          </div>
        </td>
        <td><span className="skeleton-cell" style={{ width: 90, height: 14 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 80, height: 22, borderRadius: 99 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 70, height: 22, borderRadius: 99 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 110, height: 32, borderRadius: 8 }} /></td>
        <td><span className="skeleton-cell" style={{ width: 80, height: 28, borderRadius: 99 }} /></td>
      </tr>
    ))}
  </>
);

const compareDueDates = (a, b) => {
  if (a.status === 'Completed' && b.status !== 'Completed') return 1;
  if (b.status === 'Completed' && a.status !== 'Completed') return -1;
  const aDate = a.dueDate, bDate = b.dueDate;
  if (!aDate && !bDate) return 0;
  if (!aDate) return 1;
  if (!bDate) return -1;
  const dA = new Date(aDate), dB = new Date(bDate);
  if (isNaN(dA.getTime()) && isNaN(dB.getTime())) return 0;
  if (isNaN(dA.getTime())) return 1;
  if (isNaN(dB.getTime())) return -1;
  return dA.getTime() - dB.getTime();
};

const PersonalTaxDataPage = () => {
  const portalState = useTaxPortal();
  const {
    account, msalInstance, isInitialized,
    setDialNumber, isDialerOpen, setIsDialerOpen,
    setRefreshTrigger, setEmailModalConfig,
  } = portalState;

  const navigate = useNavigate();
  const [customers,       setCustomers]       = useState([]);
  const [searchText,      setSearchText]      = useState('');
  const [sortKey,         setSortKey]         = useState('dueDate');
  const [sortAsc,         setSortAsc]         = useState(true);
  const [filterStatus,    setFilterStatus]    = useState('nonCompleted');
  const [filterAssignedTo,setFilterAssignedTo]= useState('');
  const [filterDueDate,   setFilterDueDate]   = useState('');
  const [loading,         setLoading]         = useState(false);
  const [isSaving,        setIsSaving]        = useState(false);
  const [eSignDetails,    setESignDetails]    = useState(null);
  const [expandedId,      setExpandedId]      = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [actionItemsCustomer, setActionItemsCustomer] = useState(null);
  const [pageSize,        setPageSize]        = useState(25);
  const [pageIndex,       setPageIndex]       = useState(0);

  const isDrawerOpen = !!expandedId || !!editingCustomer;

  useEffect(() => {
    document.body.classList.toggle('drawer-open', isDrawerOpen || !!actionItemsCustomer);
    return () => document.body.classList.remove('drawer-open');
  }, [isDrawerOpen, actionItemsCustomer]);

  const assigneeStats = useMemo(() => calculateAssigneeStats(customers), [customers]);

  useEffect(() => {
    if (account && isInitialized) fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isInitialized]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl('/v1/customers'), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Unable to load customer list.');
      setCustomers((await res.json()) || []);
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      console.error(e);
      toast.error('Could not verify your session. Please sign in again.');
    } finally { setLoading(false); }
  };

  const fetchEsignDetails = async (entry) => {
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const response = await fetch(getApiUrl(`/v1/staff/esign-details/Personal/${entry.id || entry._id}`), {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setESignDetails(data);
      } else {
        const errorData = await response.json();
        toast.error(`Could not fetch e-sign details: ${errorData.error || 'Not found'}`);
      }
    } catch (err) {
      console.error('Error fetching e-sign details:', err);
      toast.error('An error occurred while fetching e-sign details.');
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const handleSaveCustomer = async (formData) => {
    setIsSaving(true);
    const isEditing = !!editingCustomer;
    const id = isEditing ? getId(editingCustomer) : null;

    const cleanName = (formData.name || '').trim().toLowerCase();
    const cleanEmail = (formData.email || '').trim().toLowerCase();

    if (cleanName && cleanEmail) {
      const isDuplicate = customers.some(customer => {
        return (customer.name || '').trim().toLowerCase() === cleanName &&
               (customer.email || '').trim().toLowerCase() === cleanEmail &&
               (!isEditing || getId(customer) !== id);
      });

      if (isDuplicate) {
        toast.error('Duplicate record found with the same name and email.');
        setIsSaving(false);
        return;
      }
    }

    let customerData = { 
      ...formData, 
      mobile: formatPhoneNumber(formData.mobile),
      sinNumber: formatSinNumber(formData.sinNumber),
      updatedBy: account?.username || account?.name || 'Unknown' 
    };
    if (!isEditing) {
      if (!customerData.dueDate) {
        const d = new Date(); d.setDate(d.getDate() + 3);
        customerData.dueDate = d.toISOString().split('T')[0];
      }
    }
    const url    = isEditing ? getApiUrl(`/v1/customers/${id}`) : getApiUrl('/v1/customers');
    const method = isEditing ? 'PUT' : 'POST';
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(customerData),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Unable to save customer.'); }
      const savedRecord = await res.json();
      if (isEditing) {
        setCustomers(prev => prev.map(c => getId(c) === id ? { ...c, ...customerData } : c));
        toast.success('Customer updated successfully.');
      } else {
        setCustomers(prev => [{ ...savedRecord, ...customerData }, ...prev]);
        toast.success('Customer added successfully.');
      }
      handleCloseDrawer();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not save customer. Please try again.');
    } finally { setIsSaving(false); }
  };

  const handleAssignedToChange = async (record, assignedTo) => {
    const id = getId(record);
    const updates = { assignedTo, updatedBy: account?.username || account?.name || 'Unknown' };
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/customers/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Unable to update assignment.'); }
      await res.json();
      setCustomers(prev => prev.map(item => getId(item) === id ? { ...item, ...updates } : item));
      toast.success('Assignment updated.');
    } catch (e) {
      console.error(e);
      toast.error('Could not verify your session. Please sign in again.');
    } finally { setIsSaving(false); }
  };

  const handleDeleteCustomer = async (customer) => {
    const id = getId(customer);
    setIsSaving(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/customers/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Unable to delete customer.'); }
      setCustomers(prev => prev.filter(item => getId(item) !== id));
      toast.success('Customer record deleted.');
      handleCloseDrawer();
    } catch (e) {
      console.error(e);
      toast.error('Could not verify your session or delete record. Please sign in again.');
    } finally { setIsSaving(false); }
  };

  const handleOpenDrawerForAdd  = () => { setEditingCustomer(null); setExpandedId(true); };
  const handleOpenDrawerForEdit = (c) => { setEditingCustomer(c); setExpandedId(getId(c)); };
  const handleCloseDrawer = () => { setExpandedId(null); setEditingCustomer(null); };
  const handleOpenActionItems = (customer) => setActionItemsCustomer(customer);

  const filteredData = customers.filter((record) => {
    const assignedMatch = filterAssignedTo ? record.assignedTo === filterAssignedTo : true;
    const dueDateMatch  = filterDueDate    ? record.dueDate    === filterDueDate    : true;
    const value = `${record.name} ${record.spouse} ${record.mobile} ${record.sinNumber || ''} ${record.status} ${record.dob} ${record.workStatus} ${record.updatedBy} ${record.notes} ${record.assignedTo}`.toLowerCase();
    const statusMatch = filterStatus === 'nonCompleted'
      ? record.status !== 'Completed'
      : filterStatus
        ? record.status === filterStatus
        : true;
    return statusMatch && assignedMatch && dueDateMatch && value.includes(searchText.toLowerCase());
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortKey === 'dueDate') {
      const cmp = compareDueDates(a, b); return sortAsc ? cmp : -cmp;
    }
    const aVal = typeof a[sortKey] === 'string' ? a[sortKey].toLowerCase() : a[sortKey];
    const bVal = typeof b[sortKey] === 'string' ? b[sortKey].toLowerCase() : b[sortKey];
    if (aVal < bVal) return sortAsc ? -1 : 1;
    if (aVal > bVal) return sortAsc ?  1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const pageData   = sortedData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pageIndex > 0 && pageIndex >= totalPages) setPageIndex(totalPages - 1);
  }, [pageSize, filterStatus, searchText, customers.length, pageIndex, totalPages]);

  const handleExportToExcel = async () => {
    const XLSX = await import('xlsx');
    const exportData = sortedData.map((r) => ({
      'Name': r.name || '', 'Spouse': r.spouse || '', 'Phone number': formatPhoneNumber(r.mobile),
      'SIN Number': formatSinNumber(r.sinNumber),
      'Email': r.email || '', 'Address': r.address || '', 'City': r.city || '',
      'Status': r.status || '',
      'Due Date': r.dueDate ? getRawDateString(r.dueDate) : 'N/A',
      'Assigned To': r.assignedTo || '', 'Draft Status': r.draftStatus || '',
      'Received Date':   r.receivedDate   ? getRawDateString(r.receivedDate)   : '',
      'Invoice Date':    r.invoiceDate    ? getRawDateString(r.invoiceDate)    : '',
      'Filing Date':     r.filingDate     ? getRawDateString(r.filingDate)     : '',
      'Draft Sent Date': r.draftSentDate  ? getRawDateString(r.draftSentDate)  : '',
      'Invoice No.': r.invoiceNo || '', 'Invoice Amount': r.invoiceAmount || '',
      'Payment Received': r.paymentReceived || '',
      'DOB': r.dob ? getRawDateString(r.dob) : '',
      'Work Status': r.workStatus || '', 'Notes': r.notes || '',
      'Other Comments': r.otherComments || '',
      'Family Details': r.familyDetails || '', 'History': r.history || '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Personal Tax Data');
    const ts = new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    XLSX.writeFile(wb, `Personal_Tax_Data_${ts}.xlsx`);
  };

  // Computed stats
  const totalCount          = customers.length;
  const completedCount      = customers.filter(c => c.status === 'Completed').length;
  const nonCompletedCount   = totalCount - completedCount;
  const paymentPendingCount = customers.filter(c => c.status === 'Payment Pending').length;
  const completionPct       = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <TaxPortalLayout
      title="Personal Tax"
      description="Use your Oriental Biz account to access the personal tax portal."
      taxEntries={customers}
      portalState={portalState}
    >
      {account ? (
        <>
          {/* ══════════════ STAT CARDS ══════════════ */}
          <div className="row g-3 mb-4">

            {/* Non-completed */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#f59e0b,#d97706)', boxShadow: '2px 0 10px rgba(245,158,11,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fef3c7,#fde68a)' }}>
                    <FaFolderOpen size={20} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Non-completed</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>{nonCompletedCount}</h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1">
                  <span className="badge rounded-pill fw-medium" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: '0.68rem' }}>Action needed</span>
                </div>
              </div>
            </div>

            {/* Completed + progress bar */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#22c55e,#15803d)', boxShadow: '2px 0 10px rgba(34,197,94,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)' }}>
                    <FaCheckCircle size={20} style={{ color: '#15803d' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Completed</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>
                      {completedCount} <span className="fs-6 text-muted fw-normal">/ {totalCount}</span>
                    </h2>
                  </div>
                </div>
                <div className="position-relative z-1">
                  <div className="completion-bar-track">
                    <div className="completion-bar-fill" style={{ width: `${completionPct}%` }} />
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.68rem' }}>{completionPct}% done</small>
                </div>
              </div>
            </div>

            {/* Payment Pending */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#ef4444,#b91c1c)', boxShadow: '2px 0 10px rgba(239,68,68,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fee2e2,#fecaca)' }}>
                    <FaExclamationCircle size={20} style={{ color: '#b91c1c' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Payment Pending</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>{paymentPendingCount}</h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1">
                  <span className="badge rounded-pill fw-medium" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontSize: '0.68rem' }}>Awaiting payment</span>
                </div>
              </div>
            </div>

            {/* Team Queue */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white d-flex flex-column hover-lift stat-card position-relative overflow-hidden">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#6366f1,#4338ca)', boxShadow: '2px 0 10px rgba(99,102,241,0.3)' }} />
                <div className="d-flex align-items-center gap-2 mb-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)' }}>
                    <FaUsers size={15} style={{ color: '#4338ca' }} />
                  </div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Team queue · pending / total</small>
                </div>
                <div className="flex-grow-1 overflow-auto pe-1 position-relative z-1" style={{ maxHeight: '80px' }}>
                  {assigneeStats.map(([assignee, counts]) => (
                    <div key={assignee} className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-truncate fw-medium text-dark" style={{ fontSize: '0.78rem', maxWidth: '140px' }} title={assignee}>
                        {assignee.split('@')[0]}
                      </span>
                      <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#1e40af', border: '1px solid #e2e8f0', fontSize: '0.68rem', padding: '3px 10px' }}>
                        {counts.pending} <span className="text-muted fw-normal">/ {counts.total}</span>
                      </span>
                    </div>
                  ))}
                  {assigneeStats.length === 0 && <span className="text-muted small">No assignments yet.</span>}
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
            .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(15,23,42,0.10) !important; }
            .stat-accent { position: absolute; left: 0; top: 0; bottom: 0; width: 5px; border-radius: 4px 0 0 4px; }
            .table-modern tbody tr { transition: background-color 0.15s ease; }
            .table-modern tbody tr:hover { background-color: #f8fafc; }
            .table-modern thead th { position: sticky; top: 0; z-index: 2; user-select: none; background: #fff; }
            .table-header-sep { border-bottom: 2px solid #e2e8f0 !important; }
            .mobile-record-card { transition: box-shadow 0.15s ease, transform 0.15s ease; }
            .mobile-record-card:active { transform: scale(0.995); }
            .form-control:focus, .form-select:focus { box-shadow: 0 0 0 3px rgba(13,110,253,0.15) !important; border-color: #86b7fe !important; }
          `}</style>

          {/* ── Toolbar ── */}
          <TaxPortalToolbar theme="glass"
            searchPlaceholder="Search name, spouse, mobile..."
            searchText={searchText}
            onSearchChange={setSearchText}
            statusOptions={statusOptions}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            assignedToOptions={assignedToOptions}
            filterAssignedTo={filterAssignedTo}
            onFilterAssignedToChange={setFilterAssignedTo}
            dueDateOptions={dueDateOptions}
            filterDueDate={filterDueDate}
            onFilterDueDateChange={setFilterDueDate}
            pageSize={pageSize}
            onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
            onAdd={handleOpenDrawerForAdd}
            addLabel="Add Customer"
            onExport={handleExportToExcel}
            disableExport={sortedData.length === 0}
            onRefresh={fetchCustomers}
            isRefreshing={loading}
          />

          <div className="position-relative" style={{ minHeight: '350px' }}>

            {/* ══════════════ MOBILE CARDS ══════════════ */}
            <div className="d-block d-md-none">
              {loading ? (
                <div className="d-flex flex-column gap-3">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="card border-0 rounded-4" style={{ overflow: 'hidden' }}>
                      <div className="skeleton-cell" style={{ height: 6, borderRadius: 0 }} />
                      <div className="card-body p-3">
                        <div className="d-flex gap-2 align-items-center mb-3">
                          <span className="skeleton-cell" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                          <div className="d-flex flex-column gap-1">
                            <span className="skeleton-cell" style={{ width: 120, height: 13 }} />
                            <span className="skeleton-cell" style={{ width: 80, height: 11 }} />
                          </div>
                        </div>
                        <span className="skeleton-cell" style={{ width: '100%', height: 32, borderRadius: 8 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : pageData.length === 0 ? (
                <div className="card border-0 rounded-4 glass-card">
                  <div className="empty-state-wrap">
                    <div className="empty-state-icon"><FaInbox size={30} /></div>
                    <p className="mb-1 fw-semibold text-dark">No customers found</p>
                    <p className="mb-3 text-muted small text-center">Try adjusting your filters or search term.</p>
                    <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleOpenDrawerForAdd}>
                      <FaUserPlus className="me-2" size={12} /> Add Customer
                    </button>
                  </div>
                </div>
              ) : (
                pageData.map((record) => {
                  const recordId    = getId(record);
                  const statusMeta  = getStatusMeta(record.status);
                  const avatarColor = getAvatarColor(record.name);
                  return (
                    <div key={recordId} className="card mb-3 mobile-record-card glass-card border-0 rounded-4 position-relative">
                      <div style={{ height: 5, background: statusMeta.color, borderRadius: '1rem 1rem 0 0' }} />
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className="name-avatar flex-shrink-0" style={{ backgroundColor: avatarColor.bg, color: avatarColor.text, width: 36, height: 36, fontSize: 13 }}>
                              {getInitials(record.name)}
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">{record.name}</h6>
                              <div className="small text-muted">
                                {record.mobile ? (
                                  <button className="btn btn-link btn-sm p-0 text-decoration-none text-start" onClick={() => { setDialNumber(record.mobile); setIsDialerOpen(true); }}>
                                    <span className="d-flex align-items-center gap-1 text-muted"><FaPhoneAlt size={9} /> {formatPhoneNumber(record.mobile)}</span>
                                  </button>
                                ) : 'No phone number'}
                                {record.sinNumber && (
                                  <div className="text-muted small mt-1">SIN: {formatSinNumber(record.sinNumber)}</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <ActionMenu
                            items={[
                              { label: 'Edit Details', icon: FaEdit, color: '#1d4ed8', onClick: () => handleOpenDrawerForEdit(record) },
                              { label: 'Action Items', icon: FaClipboardList, color: '#0e7490', onClick: () => handleOpenActionItems(record) },
                              { label: 'Request Details', icon: FaClipboardList, color: '#0284c7', onClick: () => setEmailModalConfig({ customer: record, action: 'requestDetails', taxType: 'Personal' }) },
                              { label: 'Request eSign', icon: FaSignature, color: '#6d28d9', onClick: () => navigate('/esign-request', { state: { customerId: recordId, clientName: record.name, clientEmail: record.email, taxType: 'Personal' } }) },
                              { label: 'eSign Details', icon: FaChartBar, color: '#0d9488', onClick: () => fetchEsignDetails(record) },
                              { divider: true },
                              { label: 'Create Invoice', icon: FaFileInvoiceDollar, color: '#15803d', onClick: () => navigate('/invoice', { state: { customer: record, taxType: 'Personal' } }) },
                              { label: 'Request Document', icon: FaFolderOpen, color: '#64748b', onClick: () => setEmailModalConfig({ customer: record, action: 'requestDocument', taxType: 'Personal' }) },
                              { label: 'Send Confirmation', icon: FaPaperPlane, color: '#2563eb', onClick: () => setEmailModalConfig({ customer: record, action: 'sendConfirmation', taxType: 'Personal' }) },
                            ]}
                          />
                        </div>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <StatusBadge status={record.status} />
                          <DueDatePill dueDate={record.dueDate} status={record.status} />
                        </div>
                        <select className="form-select form-select-sm glass-input shadow-sm rounded-3 py-2" value={record.assignedTo || ''} disabled={isSaving} onChange={(e) => handleAssignedToChange(record, e.target.value)}>
                          <option value="">Unassigned</option>
                          {assignedToOptions.map(email => <option key={email} value={email}>{email}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ══════════════ DESKTOP TABLE ══════════════ */}
            <div className="table-responsive d-none d-md-block rounded-4 shadow-sm bg-white" style={{ minHeight: '350px' }}>
              <table className="table table-modern table-hover align-middle mb-0" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  {/*
                    KEY ALIGNMENT FIX:
                    The td has ps-4 (24px) + avatar (32px) + gap-2 (8px) = 64px before name text.
                    So the th must also start its text at 64px = paddingLeft: NAME_TH_PL (64px).
                  */}
                  <tr className="table-header-sep text-muted text-uppercase" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    <th className="py-3 border-0" style={{ paddingLeft: NAME_TH_PL, cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      NAME <SortIcon active={sortKey === 'name'} asc={sortAsc} />
                    </th>
                    <th className="py-3 d-none d-md-table-cell border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('mobile')}>MOBILE <SortIcon active={sortKey === 'mobile'} asc={sortAsc} /></th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>STATUS <SortIcon active={sortKey === 'status'} asc={sortAsc} /></th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('dueDate')}>DUE DATE <SortIcon active={sortKey === 'dueDate'} asc={sortAsc} /></th>
                    <th className="py-3 d-none d-md-table-cell border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('assignedTo')}>ASSIGNED TO <SortIcon active={sortKey === 'assignedTo'} asc={sortAsc} /></th>
                    <th className="py-3 border-0">ACTION</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {loading ? (
                    <SkeletonRows count={pageSize > 10 ? 8 : 5} />
                  ) : pageData.length === 0 ? (
                    <tr>
                      <td colSpan="6">
                        <div className="empty-state-wrap">
                          <div className="empty-state-icon"><FaInbox size={30} /></div>
                          <p className="mb-1 fw-semibold text-dark">No customers found</p>
                          <p className="mb-3 text-muted small text-center" style={{ maxWidth: 320 }}>
                            No records match your current filters. Try a different search, status, or assignee.
                          </p>
                          <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleOpenDrawerForAdd}>
                            <FaUserPlus className="me-2" size={12} /> Add Customer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageData.map((record) => {
                      const recordId = getId(record);
                      return (
                        <React.Fragment key={recordId}>
                          <tr>
                            {/* ps-4 (24px) + avatar (32px) + gap-2 (8px) = 64px → name text at 64px from left */}
                            <td className="ps-4" style={{ overflow: 'hidden' }}>
                              <div className="d-flex align-items-center gap-2">
                                <NameAvatar name={record.name || ''} />
                                {/* minWidth:0 lets the flex child shrink so text-truncate works */}
                                <div style={{ minWidth: 0 }}>
                                  <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={record.name}>{record.name}</div>
                                  {record.spouse && (
                                    <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }} title={record.spouse}>{record.spouse}</div>
                                  )}
                                  {record.sinNumber && (
                                    <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }} title={`SIN: ${formatSinNumber(record.sinNumber)}`}>SIN: {formatSinNumber(record.sinNumber)}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="d-none d-md-table-cell">
                              {record.mobile ? (
                                <button className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: '0.82rem' }} onClick={() => { setDialNumber(record.mobile); setIsDialerOpen(true); }}>
                                  <span className="d-flex align-items-center gap-2"><FaPhoneAlt size={10} /> {formatPhoneNumber(record.mobile)}</span>
                                </button>
                              ) : <span className="text-muted">—</span>}
                            </td>
                            <td><StatusBadge status={record.status} /></td>
                            <td><DueDatePill dueDate={record.dueDate} status={record.status} /></td>
                            <td className="d-none d-md-table-cell">
                              <select className="form-select form-select-sm glass-input shadow-sm rounded-3 py-2" value={record.assignedTo || ''} disabled={isSaving} onChange={(e) => handleAssignedToChange(record, e.target.value)}>
                                <option value="">Unassigned</option>
                                {assignedToOptions.map(email => <option key={email} value={email}>{email}</option>)}
                              </select>
                            </td>
                            <td>
                              <ActionMenu
                                items={[
                                  { label: 'Edit Details', icon: FaEdit, color: '#1d4ed8', onClick: () => handleOpenDrawerForEdit(record) },
                                  { label: 'Action Items', icon: FaClipboardList, color: '#0e7490', onClick: () => handleOpenActionItems(record) },
                                  { label: 'Request eSign', icon: FaSignature, color: '#6d28d9', onClick: () => navigate('/esign-request', { state: { customerId: recordId, clientName: record.name, clientEmail: record.email, taxType: 'Personal' } }) },
                                  { label: 'eSign Details', icon: FaChartBar, color: '#0e7490', onClick: () => fetchEsignDetails(record) },
                                  { label: 'Create Invoice', icon: FaFileInvoiceDollar, color: '#15803d', onClick: () => navigate('/invoice', { state: { customer: record, taxType: 'Personal' } }) },
                                  { divider: true },
                                  { label: 'Request Document', icon: FaFolderOpen, color: '#64748b', onClick: () => setEmailModalConfig({ customer: record, action: 'requestDocument', taxType: 'Personal' }) },
                              { label: 'Send Confirmation', icon: FaPaperPlane, color: '#0e7490', onClick: () => setEmailModalConfig({ customer: record, action: 'sendConfirmation', taxType: 'Personal' }) },
                                ]}
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ══════════════ PAGINATION ══════════════ */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-4 px-1">
              {/* Record counter */}
              <div className="d-flex align-items-center gap-2">
                <span
                  className="rounded-pill px-3 py-1 fw-semibold"
                  style={{ background: '#f1f5f9', color: '#374151', fontSize: '0.78rem', border: '1px solid #e2e8f0' }}
                >
                  {sortedData.length === 0 ? '0' : `${pageIndex * pageSize + 1}–${Math.min(sortedData.length, (pageIndex + 1) * pageSize)}`}
                  <span className="text-muted fw-normal"> of </span>
                  {sortedData.length}
                </span>
                <span className="text-muted" style={{ fontSize: '0.78rem' }}>records</span>
              </div>

              {/* Page buttons */}
              <div className="d-flex align-items-center gap-1">
                {/* Prev */}
                <button
                  className="page-pill"
                  disabled={pageIndex === 0}
                  onClick={() => setPageIndex(p => Math.max(p - 1, 0))}
                  aria-label="Previous page"
                >
                  <FaChevronLeft size={10} /> Prev
                </button>

                {/* Smart page numbers with ellipsis */}
                {(() => {
                  if (totalPages <= 1) return null;
                  const items = [];
                  const addPage = (i) => items.push(
                    <button
                      key={i}
                      className={`page-pill${i === pageIndex ? ' active' : ''}`}
                      onClick={() => setPageIndex(i)}
                      aria-label={`Page ${i + 1}`}
                      aria-current={i === pageIndex ? 'page' : undefined}
                    >
                      {i + 1}
                    </button>
                  );
                  const addDots = (key) => items.push(<span key={key} className="page-dots">···</span>);

                  if (totalPages <= 7) {
                    for (let i = 0; i < totalPages; i++) addPage(i);
                  } else {
                    addPage(0);
                    if (pageIndex > 2) addDots('left');
                    for (let i = Math.max(1, pageIndex - 1); i <= Math.min(totalPages - 2, pageIndex + 1); i++) addPage(i);
                    if (pageIndex < totalPages - 3) addDots('right');
                    addPage(totalPages - 1);
                  }
                  return items;
                })()}

                {/* Next */}
                <button
                  className="page-pill"
                  disabled={pageIndex >= totalPages - 1}
                  onClick={() => setPageIndex(p => Math.min(p + 1, totalPages - 1))}
                  aria-label="Next page"
                >
                  Next <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : <div className="alert alert-info">Please sign in with your Oriental Biz account to view the customer table.</div>}

      <CustomerDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} customer={editingCustomer} onSave={handleSaveCustomer} onDelete={handleDeleteCustomer} saving={isSaving} isAdmin={isAdminRole(account?.username)} isDialerOpen={isDialerOpen} />
      <ActionItemsDrawer isOpen={!!actionItemsCustomer} onClose={() => setActionItemsCustomer(null)} customer={actionItemsCustomer} customerType="Personal" account={account} msalInstance={msalInstance} isDialerOpen={isDialerOpen} />
      <ESignDetailsModal details={eSignDetails} onClose={() => setESignDetails(null)} />

      <Loader variant="overlay" show={isSaving && !loading} text="Saving changes..." />
    </TaxPortalLayout>
  );
};

export default PersonalTaxDataPage;